import { useEffect } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于开云电竞 - 专业电竞平台',
    description: '开云电竞是面向电竞爱好者的专业竞技平台，提供丰富的电竞赛事、实时数据与竞技数据服务。了解我们的品牌故事、平台信誉与联系方式。',
    url: 'https://www.kaiyunesports.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '开云电竞',
      url: 'https://www.kaiyunesports.com',
    },
    about: {
      '@type': 'Organization',
      name: '开云电竞',
      description: '开云电竞是面向电竞爱好者的专业竞技平台，提供丰富的电竞赛事、实时赔率与竞技数据服务。',
      url: 'https://www.kaiyunesports.com',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-9999',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '开云电竞是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开云电竞是面向电竞爱好者的专业竞技平台，提供覆盖英雄联盟、CS2、DOTA2、王者荣耀等热门游戏的赛事信息、实时数据与深度分析服务。我们致力于为广大电竞爱好者提供安全稳定、数据准确的竞技体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何注册开云电竞账号？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '注册开云电竞账号非常简单，您只需点击平台首页的"注册体验"按钮，填写基本信息即可完成注册。整个过程仅需1-2分钟，注册后即可享受全部赛事服务。',
        },
      },
      {
        '@type': 'Question',
        name: '开云电竞平台的安全性如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开云电竞采用国际领先的数据加密技术，严格遵守相关法规，确保用户数据安全和账户资金安全。我们拥有完善的隐私保护机制和安全审核体系。',
        },
      },
      {
        '@type': 'Question',
        name: '开云电竞支持哪些游戏赛事？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '开云电竞支持包括英雄联盟（LPL/LCK/LEC）、CS2（IEM/ESL/BLAST）、DOTA2（TI/Major）、王者荣耀（KPL/世冠）、Valorant（VCT）、PUBG（PGC/PGS）等全球主流电竞赛事。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系开云电竞客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们的客服团队：客服热线400-888-9999（24小时），官方邮箱support@kaiyunesports.com，或通过网站的联系表单提交您的疑问，我们将在第一时间为您解答。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 开云电竞 | 品牌介绍、平台信誉与联系方式"
        description="了解开云电竞的品牌故事、平台信誉与联系方式。开云电竞是面向电竞爱好者的专业竞技平台，安全稳定、注册便捷，服务百万电竞用户。"
        keywords="开云电竞,关于开云电竞,电竞平台信誉,电竞平台联系方式,开云电竞品牌"
        canonicalUrl="https://www.kaiyunesports.com/about"
        schemaData={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '关于我们' },
          ]}
        />

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
                关于开云电竞
              </h1>
              <p className="text-foreground-600 text-sm mt-3 max-w-2xl mx-auto">
                专业电竞平台，致力于为全球电竞爱好者提供最优质的竞技服务
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5 font-heading">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-700 text-sm leading-relaxed">
                  <p>
                    开云电竞成立于2020年，是国内领先的专业电竞竞技平台。自成立以来，我们始终秉持"专业、安全、公正"的核心价值观，致力于为全球电竞爱好者提供一流的赛事数据与竞技服务。
                  </p>
                  <p>
                    经过多年发展，开云电竞已覆盖英雄联盟、CS2、DOTA2、王者荣耀、Valorant、PUBG等全球主流电竞项目，年均覆盖赛事超过200场，注册用户突破500万。
                  </p>
                  <p>
                    我们与全球顶级电竞赛事组织、战队和数据供应商建立了深度合作关系，确保为每一位用户提供最准确、最及时的赛事信息与数据分析。无论是赛事直播、实时数据还是深度分析，开云电竞都是您最可靠的电竞伙伴。
                  </p>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden h-64 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20esports%20company%20office%20with%20gaming%20setups%20and%20team%20collaboration%2C%20professional%20environment%2C%20warm%20lighting%2C%20clean%20aesthetic&width=800&height=500&seq=about-brand-01&orientation=landscape"
                  alt="开云电竞品牌故事"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center font-heading">
                平台优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { icon: 'ri-shield-check-line', title: '安全稳定', desc: '采用国际领先加密技术，全方位保障用户数据与资金安全' },
                  { icon: 'ri-flashlight-line', title: '数据精准', desc: '实时赛事数据更新，准确率高达99.9%，为用户提供最可靠的参考' },
                  { icon: 'ri-customer-service-2-line', title: '服务专业', desc: '7×24小时专业客服团队，随时解答您的疑问' },
                  { icon: 'ri-rocket-line', title: '注册便捷', desc: '一分钟快速注册，即刻开启您的电竞竞技之旅' },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${item.icon} text-xl text-primary-600`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center font-heading">
                平台信誉
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-award-line text-accent-500"></i> 合规运营
                  </h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <p>开云电竞严格遵守国家相关法律法规，持有合法运营资质，确保平台运营的合规性与透明度。</p>
                    <p>我们定期接受第三方独立审计，确保所有数据的公正性和准确性。平台运营信息定期向社会公开，接受公众监督。</p>
                  </div>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-star-line text-accent-500"></i> 用户口碑
                  </h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <p>开云电竞累计服务超过500万注册用户，用户满意度持续保持在98%以上。</p>
                    <p>我们荣获多项行业大奖，包括"2025年度最佳电竞平台"、"2026年最受用户信赖电竞品牌"等荣誉。</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-heading">
                  联系我们
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">客服热线</p>
                      <p className="text-sm text-foreground-600">400-888-9999（24小时）</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">support@kaiyunesports.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">公司地址</p>
                      <p className="text-sm text-foreground-600">上海市浦东新区电竞产业园区开云大厦</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-heading">
                  在线留言
                </h2>
                <form
                  className="space-y-4"
                  id="contact-form"
                  data-readdy-form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    const formData = new FormData(form);

                    const honeypot = document.getElementById('phone_alt') as HTMLInputElement;
                    if (honeypot && honeypot.value.trim() !== '') {
                      alert('提交成功，我们将尽快与您联系！');
                      form.reset();
                      return;
                    }

                    try {
                      const response = await fetch('https://readdy.ai/api/form/d9rfoaee3oq4jqumu0rg', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                      });
                      const responseText = await response.text();
                      if (response.ok) {
                        alert('提交成功，我们将尽快与您联系！');
                        form.reset();
                      } else {
                        alert('提交失败：' + (responseText || '请稍后重试'));
                      }
                    } catch {
                      alert('网络错误，请稍后重试');
                    }
                  }}
                >
                  <input
                    type="text"
                    name="phone_alt"
                    id="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <input
                    type="text"
                    name="subject"
                    placeholder="主题"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <textarea
                    name="message"
                    placeholder="请输入您的留言内容..."
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}